import { Grid, Card, Text, Title, Group, Stack, RingProgress, Badge, Paper, rem } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import {
  IconServer,
  IconNetwork,
  IconCpu,
  IconDatabase,
  IconActivity,
  IconAlertCircle,
} from '@tabler/icons-react'
import { clusterApi, servicesApi, nodesApi } from '@/api'
import { StatsCard } from '@/components/StatsCard'
import { ClusterMetrics } from '@/components/ClusterMetrics'
import { ServiceList } from '@/components/ServiceList'
import { NodeStatus } from '@/components/NodeStatus'
import { RecentEvents } from '@/components/RecentEvents'
import { formatBytes } from '@/utils/format'

export function Dashboard() {
  // Fetch cluster stats
  const { data: clusterStats, isLoading: statsLoading } = useQuery({
    queryKey: ['cluster', 'stats'],
    queryFn: async () => {
      const { data } = await clusterApi.getStats()
      return data
    },
    refetchInterval: 5000,
  })

  // Fetch services
  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data } = await servicesApi.list()
      return data
    },
    refetchInterval: 10000,
  })

  // Fetch nodes
  const { data: nodes = [] } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => {
      const { data } = await nodesApi.list()
      return data
    },
    refetchInterval: 10000,
  })

  const healthyNodes = nodes.filter((n) => n.Status.State === 'ready').length
  const runningServices = services.filter((s) => s.Spec.Mode.Replicated).length

  return (
    <Stack gap="lg">
      <Title order={2}>Cluster Overview</Title>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Nodes"
            value={clusterStats?.nodes || 0}
            subtitle={`${healthyNodes} healthy`}
            icon={<IconNetwork size={24} />}
            color="blue"
            loading={statsLoading}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Services"
            value={clusterStats?.services || 0}
            subtitle={`${runningServices} running`}
            icon={<IconServer size={24} />}
            color="green"
            loading={statsLoading}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Containers"
            value={clusterStats?.containers || 0}
            subtitle="Active"
            icon={<IconDatabase size={24} />}
            color="violet"
            loading={statsLoading}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="CPU Cores"
            value={Math.floor(clusterStats?.cpu_total || 0)}
            subtitle={`${formatBytes(clusterStats?.memory_total || 0)} RAM`}
            icon={<IconCpu size={24} />}
            color="orange"
            loading={statsLoading}
          />
        </Grid.Col>
      </Grid>

      {/* Cluster Health */}
      <Card shadow="sm" p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Text size="lg" fw={600}>
            Cluster Health
          </Text>
          <Badge
            color={clusterStats?.status === 'healthy' ? 'green' : 'red'}
            variant="dot"
            size="lg"
          >
            {clusterStats?.status || 'Unknown'}
          </Badge>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" withBorder>
              <Group justify="space-between">
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Manager Nodes
                  </Text>
                  <Text size="xl" fw={700}>
                    {nodes.filter((n) => n.Spec.Role === 'manager').length}
                  </Text>
                </Stack>
                <RingProgress
                  size={80}
                  thickness={8}
                  sections={[
                    {
                      value:
                        (nodes.filter((n) => n.Spec.Role === 'manager' && n.Status.State === 'ready')
                          .length /
                          Math.max(nodes.filter((n) => n.Spec.Role === 'manager').length, 1)) *
                        100,
                      color: 'green',
                    },
                  ]}
                />
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" withBorder>
              <Group justify="space-between">
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Worker Nodes
                  </Text>
                  <Text size="xl" fw={700}>
                    {nodes.filter((n) => n.Spec.Role === 'worker').length}
                  </Text>
                </Stack>
                <RingProgress
                  size={80}
                  thickness={8}
                  sections={[
                    {
                      value:
                        (nodes.filter((n) => n.Spec.Role === 'worker' && n.Status.State === 'ready')
                          .length /
                          Math.max(nodes.filter((n) => n.Spec.Role === 'worker').length, 1)) *
                        100,
                      color: 'blue',
                    },
                  ]}
                />
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper p="md" withBorder>
              <Group justify="space-between">
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    Resource Usage
                  </Text>
                  <Group gap="xs">
                    <IconCpu size={20} />
                    <Text size="sm">45%</Text>
                    <IconDatabase size={20} />
                    <Text size="sm">62%</Text>
                  </Group>
                </Stack>
                <IconActivity size={40} color="var(--mantine-color-green-6)" />
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>
      </Card>

      {/* Main Content Grid */}
      <Grid>
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="lg">
            {/* Cluster Metrics */}
            <ClusterMetrics />

            {/* Service List */}
            <ServiceList services={services.slice(0, 5)} />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg">
            {/* Node Status */}
            <NodeStatus nodes={nodes} />

            {/* Recent Events */}
            <RecentEvents />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}